<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from "axios";
import Swal from "sweetalert2";
import { usePermissions } from "@/composables/usePermissions";

export default {
    page: {
        title: "Credit Segmentation Configuration",
        meta: [{ name: "description", content: appConfig.description }],
    },

    data() {
        return {
            title: "Credit Segmentation Configuration",
            items: [
                { text: "Settings", href: "/" },
                { text: "Credit Segmentation Configuration", active: true },
            ],

            // Program dropdown
            programList: [],
            selectedProgramId: null,
            selectedProgramName: "",
            loadingPrograms: false,

            // Configurations state
            loadingConfigs: false,
            savingConfigs: false,

            // Credit Score: grouped by tenure { 12: { MIN_CREDIT_SCORE }, 24: {...}, 36: {...} } plus V2 (flat)
            creditScoreConfig: {
                12: { MIN_CREDIT_SCORE: "" },
                24: { MIN_CREDIT_SCORE: "" },
                36: { MIN_CREDIT_SCORE: "" },
                MIN_CREDIT_SCORE_V2: "",
            },

            // EPF: grouped by tenure { 12: { MIN_SALARY }, 24: {...}, 36: {...} } plus V2 (flat)
            epfConfig: {
                12: { MIN_SALARY: "" },
                24: { MIN_SALARY: "" },
                36: { MIN_SALARY: "" },
                MIN_SALARY_V2: "",
            },

            // Payslip: flat (no tenure)
            payslipConfig: {
                PAYSLIP_DSR_THRESHOLD: "",
                PAYSLIP_DSR_THRESHOLD_V2: "",
                PAYSLIP_MIN_SALARY: "",
            },

            // Bank Statement: flat (no tenure)
            bankStatementConfig: {
                BANK_STATEMENT_DSR_THRESHOLD: "",
                BANK_STATEMENT_DSR_THRESHOLD_V2: "",
                BANK_STATEMENT_MIN_TOTAL_INFLOW: "",
            },

            // Max Monthly Affordable: flat (no tenure)
            maxMonthlyAffordableConfig: {
                MAX_MONTHLY_AFFORDABLE_PERCENTAGE: "",
            },
        };
    },

    setup() {
        const permissions = usePermissions();
        return { permissions };
    },

    methods: {
        // --- Step 1: Load programs dropdown ---
        getPrograms() {
            this.loadingPrograms = true;
            axios.defaults.headers.common["authorization"] =
                "Bearer " + localStorage.getItem("jwt");
            axios
                .get(process.env.VUE_APP_API_BASEURL + "credit-segmentation/programs")
                .then((response) => {
                    if (response.data && response.data.programs) {
                        this.programList = response.data.programs;
                    }
                })
                .catch((error) => {
                    console.error("Error loading programs:", error);
                    Swal.fire({
                        title: "Error",
                        text: error.response?.data?.message || "Failed to load programs.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                })
                .finally(() => {
                    this.loadingPrograms = false;
                });
        },

        // --- Step 2: Load configurations when a program is selected ---
        onProgramChange() {
            if (!this.selectedProgramId) return;
            this.loadingConfigs = true;
            this.resetConfigs();
            axios.defaults.headers.common["authorization"] =
                "Bearer " + localStorage.getItem("jwt");
            axios
                .get(
                    process.env.VUE_APP_API_BASEURL +
                        "credit-segmentation/configurations/" +
                        this.selectedProgramId
                )
                .then((response) => {
                    if (response.data && response.data.configurations) {
                        this.selectedProgramName = response.data.program_name || "";
                        this.parseConfigurations(response.data.configurations);
                    }
                })
                .catch((error) => {
                    console.error("Error loading configurations:", error);
                    Swal.fire({
                        title: "Error",
                        text:
                            error.response?.data?.message ||
                            "Failed to load configurations.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                })
                .finally(() => {
                    this.loadingConfigs = false;
                });
        },

        // --- Step 3: Group configurations into Credit Score, EPF, Payslip, Bank Statement, and Max Monthly Affordable sections ---
        parseConfigurations(configs) {
            const creditScoreKeys = ["MIN_CREDIT_SCORE"];
            const epfKeys = ["MIN_SALARY"];
            const payslipKeys = [
                "PAYSLIP_DSR_THRESHOLD",
                "PAYSLIP_DSR_THRESHOLD_V2",
                "PAYSLIP_MIN_SALARY",
            ];
            const bankStatementKeys = [
                "BANK_STATEMENT_DSR_THRESHOLD",
                "BANK_STATEMENT_DSR_THRESHOLD_V2",
                "BANK_STATEMENT_MIN_TOTAL_INFLOW",
            ];
            const maxMonthlyAffordableKeys = ["MAX_MONTHLY_AFFORDABLE_PERCENTAGE"];

            configs.forEach((config) => {
                if (config.key === "MIN_CREDIT_SCORE_V2") {
                    this.creditScoreConfig.MIN_CREDIT_SCORE_V2 = parseFloat(config.value);
                } else if (config.key === "MIN_SALARY_V2") {
                    this.epfConfig.MIN_SALARY_V2 = parseFloat(config.value);
                } else if (creditScoreKeys.includes(config.key) && config.tenure !== null) {
                    const tenure = config.tenure;
                    if (!this.creditScoreConfig[tenure]) {
                        this.creditScoreConfig[tenure] = { MIN_CREDIT_SCORE: "" };
                    }
                    this.creditScoreConfig[tenure][config.key] = parseFloat(config.value);
                } else if (epfKeys.includes(config.key) && config.tenure !== null) {
                    const tenure = config.tenure;
                    if (!this.epfConfig[tenure]) {
                        this.epfConfig[tenure] = { MIN_SALARY: "" };
                    }
                    this.epfConfig[tenure][config.key] = parseFloat(config.value);
                } else if (payslipKeys.includes(config.key)) {
                    this.payslipConfig[config.key] = parseFloat(config.value);
                } else if (bankStatementKeys.includes(config.key)) {
                    this.bankStatementConfig[config.key] = parseFloat(config.value);
                } else if (maxMonthlyAffordableKeys.includes(config.key)) {
                    this.maxMonthlyAffordableConfig[config.key] = parseFloat(config.value);
                }
            });
        },

        // --- Step 4: Save / Bulk update ---
        saveConfigurations() {
            if (!this.selectedProgramId) return;

            const configurations = [];

            // Build Credit Score payload
            Object.keys(this.creditScoreConfig).forEach((key) => {
                if (key === "MIN_CREDIT_SCORE_V2") {
                    const val = this.creditScoreConfig.MIN_CREDIT_SCORE_V2;
                    if (val !== "" && val !== null && !isNaN(val)) {
                        configurations.push({
                            key: "MIN_CREDIT_SCORE_V2",
                            tenure: 36,
                            value: parseFloat(val),
                        });
                    }
                } else {
                    const tenure = key;
                    const group = this.creditScoreConfig[tenure];
                    ["MIN_CREDIT_SCORE"].forEach((k) => {
                        if (group && group[k] !== "" && group[k] !== null && !isNaN(group[k])) {
                            configurations.push({
                                key: k,
                                tenure: parseInt(tenure),
                                value: parseFloat(group[k]),
                            });
                        }
                    });
                }
            });

            // Build EPF payload
            Object.keys(this.epfConfig).forEach((key) => {
                if (key === "MIN_SALARY_V2") {
                    const val = this.epfConfig.MIN_SALARY_V2;
                    if (val !== "" && val !== null && !isNaN(val)) {
                        configurations.push({
                            key: "MIN_SALARY_V2",
                            tenure: 36,
                            value: parseFloat(val),
                        });
                    }
                } else {
                    const tenure = key;
                    const group = this.epfConfig[tenure];
                    ["MIN_SALARY"].forEach((k) => {
                        if (group && group[k] !== "" && group[k] !== null && !isNaN(group[k])) {
                            configurations.push({
                                key: k,
                                tenure: parseInt(tenure),
                                value: parseFloat(group[k]),
                            });
                        }
                    });
                }
            });

            // Build Payslip payload
            [
                "PAYSLIP_DSR_THRESHOLD",
                "PAYSLIP_DSR_THRESHOLD_V2",
                "PAYSLIP_MIN_SALARY",
            ].forEach((key) => {
                if (this.payslipConfig[key] !== "" && this.payslipConfig[key] !== null && !isNaN(this.payslipConfig[key])) {
                    configurations.push({
                        key,
                        tenure: key === "PAYSLIP_MIN_SALARY" ? 36 : null,
                        value: parseFloat(this.payslipConfig[key]),
                    });
                }
            });

            // Build Bank Statement payload
            [
                "BANK_STATEMENT_DSR_THRESHOLD",
                "BANK_STATEMENT_DSR_THRESHOLD_V2",
                "BANK_STATEMENT_MIN_TOTAL_INFLOW",
            ].forEach((key) => {
                if (this.bankStatementConfig[key] !== "" && this.bankStatementConfig[key] !== null && !isNaN(this.bankStatementConfig[key])) {
                    configurations.push({
                        key,
                        tenure: key === "BANK_STATEMENT_MIN_TOTAL_INFLOW" ? 36 : null,
                        value: parseFloat(this.bankStatementConfig[key]),
                    });
                }
            });

            // Build Max Monthly Affordable payload
            ["MAX_MONTHLY_AFFORDABLE_PERCENTAGE"].forEach((key) => {
                if (
                    this.maxMonthlyAffordableConfig[key] !== "" &&
                    this.maxMonthlyAffordableConfig[key] !== null &&
                    !isNaN(this.maxMonthlyAffordableConfig[key])
                ) {
                    configurations.push({
                        key,
                        tenure: null,
                        value: parseFloat(this.maxMonthlyAffordableConfig[key]),
                    });
                }
            });

            this.savingConfigs = true;
            axios.defaults.headers.common["authorization"] =
                "Bearer " + localStorage.getItem("jwt");
            axios
                .post(
                    process.env.VUE_APP_API_BASEURL +
                        "credit-segmentation/configurations/" +
                        this.selectedProgramId,
                    { configurations }
                )
                .then((response) => {
                    Swal.fire({
                        title: "Saved!",
                        text:
                            response.data?.message ||
                            "Configurations updated successfully.",
                        icon: "success",
                        showConfirmButton: false,
                        timer: 1500,
                    });
                })
                .catch((error) => {
                    Swal.fire({
                        title: "Error",
                        text:
                            error.response?.data?.message ||
                            "Failed to save configurations.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                })
                .finally(() => {
                    this.savingConfigs = false;
                });
        },

        resetConfigs() {
            this.creditScoreConfig = {
                12: { MIN_CREDIT_SCORE: "" },
                24: { MIN_CREDIT_SCORE: "" },
                36: { MIN_CREDIT_SCORE: "" },
                MIN_CREDIT_SCORE_V2: "",
            };
            this.epfConfig = {
                12: { MIN_SALARY: "" },
                24: { MIN_SALARY: "" },
                36: { MIN_SALARY: "" },
                MIN_SALARY_V2: "",
            };
            this.payslipConfig = {
                PAYSLIP_DSR_THRESHOLD: "",
                PAYSLIP_DSR_THRESHOLD_V2: "",
                PAYSLIP_MIN_SALARY: "",
            };
            this.bankStatementConfig = {
                BANK_STATEMENT_DSR_THRESHOLD: "",
                BANK_STATEMENT_DSR_THRESHOLD_V2: "",
                BANK_STATEMENT_MIN_TOTAL_INFLOW: "",
            };
            this.maxMonthlyAffordableConfig = {
                MAX_MONTHLY_AFFORDABLE_PERCENTAGE: "",
            };
        },
    },

    computed: {
        epfTenures() {
            const epfTenures = Object.keys(this.epfConfig)
                .filter((k) => !isNaN(Number(k)))
                .map(Number);
            const csTenures = Object.keys(this.creditScoreConfig)
                .filter((k) => !isNaN(Number(k)))
                .map(Number);
            const allTenures = Array.from(new Set([...epfTenures, ...csTenures]));
            return allTenures.sort((a, b) => a - b);
        },
    },

    mounted() {
        this.getPrograms();
    },

    components: {
        Layout,
        PageHeader,
    },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-lg-10 mx-auto">
        <div class="card">
          <div class="card-body">

            <!-- Program Selection -->
            <div class="row mb-4">
              <div class="col-md-6">
                <label for="program_select" class="form-label fw-semibold">
                  Select Program <span class="text-danger">*</span>
                </label>
                <select
                  id="program_select"
                  v-model="selectedProgramId"
                  class="form-select"
                  @change="onProgramChange"
                  :disabled="loadingPrograms"
                >
                  <option :value="null">--- Select Program ---</option>
                  <option
                    v-for="program in programList"
                    :key="program.id"
                    :value="program.id"
                  >
                    {{ program.program_name }}
                  </option>
                </select>
                <div v-if="loadingPrograms" class="text-muted fs-12 mt-1">
                  <i class="ri-loader-4-line me-1"></i> Loading programs...
                </div>
              </div>
            </div>

            <!-- Loading configs state -->
            <div v-if="loadingConfigs" class="text-center py-5">
              <i class="ri-loader-4-line fs-24 text-primary animate-spin"></i>
              <p class="text-muted mt-2">Loading configurations...</p>
            </div>

            <!-- Configurations Form -->
            <div v-else-if="selectedProgramId && !loadingConfigs">

              <hr class="mb-4" />

              <!-- Credit Score Section -->
              <div class="mb-4">
                <h6 class="text-uppercase text-muted fw-semibold mb-1">
                  <i class="ri-user-star-line me-1"></i> Credit Score Settings
                </h6>
                <p class="text-muted fs-12 mb-3">
                  Set the minimum credit score required for each contract tenure (12, 24, 36 months) and for Order V2.
                </p>

                <div class="table-responsive mb-3">
                  <table class="table table-bordered table-sm align-middle text-center mb-0">
                    <thead class="table-light">
                      <tr>
                        <th class="text-start">Tenure (Months)</th>
                        <th>Min Credit Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="tenure in epfTenures" :key="tenure">
                        <td class="text-start fw-semibold">{{ tenure }} months</td>
                        <td>
                          <input
                            type="number"
                            class="form-control form-control-sm text-center"
                            v-model="creditScoreConfig[tenure].MIN_CREDIT_SCORE"
                            placeholder="e.g. 850"
                            min="0"
                            step="0.01"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Min Credit Score V2 (36 months)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="creditScoreConfig.MIN_CREDIT_SCORE_V2"
                      placeholder="e.g. 600"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- EPF Section -->
              <div class="mb-4">
                <h6 class="text-uppercase text-muted fw-semibold mb-1">
                  <i class="ri-file-text-line me-1"></i> EPF Settings
                </h6>
                <p class="text-muted fs-12 mb-3">
                  Set the minimum salary required for EPF document verification based on contract tenure and for Order V2.
                </p>

                <div class="table-responsive mb-3">
                  <table class="table table-bordered table-sm align-middle text-center mb-0">
                    <thead class="table-light">
                      <tr>
                        <th class="text-start">Tenure (Months)</th>
                        <th>Min Salary (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="tenure in epfTenures" :key="tenure">
                        <td class="text-start fw-semibold">{{ tenure }} months</td>
                        <td>
                          <input
                            type="number"
                            class="form-control form-control-sm text-center"
                            v-model="epfConfig[tenure].MIN_SALARY"
                            placeholder="e.g. 1000"
                            min="0"
                            step="0.01"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Min Salary V2 (36 months) (RM)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="epfConfig.MIN_SALARY_V2"
                      placeholder="e.g. 2000"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- Payslip Section -->
              <div class="mb-4">
                <h6 class="text-uppercase text-muted fw-semibold mb-1">
                  <i class="ri-receipt-line me-1"></i> Payslip Settings
                </h6>
                <p class="text-muted fs-12 mb-3">
                  Configure Debt Service Ratio (DSR) threshold percentages for Order V1/V2 and minimum salary for payslip verification.
                </p>

                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">DSR Threshold (%)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="payslipConfig.PAYSLIP_DSR_THRESHOLD"
                      placeholder="e.g. 60"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">DSR Threshold V2 (%)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="payslipConfig.PAYSLIP_DSR_THRESHOLD_V2"
                      placeholder="e.g. 60"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Min Salary (36 months) (RM)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="payslipConfig.PAYSLIP_MIN_SALARY"
                      placeholder="e.g. 2200"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- Bank Statement Section -->
              <div class="mb-4">
                <h6 class="text-uppercase text-muted fw-semibold mb-1">
                  <i class="ri-bank-line me-1"></i> Bank Statement Settings
                </h6>
                <p class="text-muted fs-12 mb-3">
                  Configure Debt Service Ratio (DSR) threshold percentages for Order V1/V2 and minimum total inflow for bank statement verification.
                </p>

                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">DSR Threshold (%)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="bankStatementConfig.BANK_STATEMENT_DSR_THRESHOLD"
                      placeholder="e.g. 60"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">DSR Threshold V2 (%)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="bankStatementConfig.BANK_STATEMENT_DSR_THRESHOLD_V2"
                      placeholder="e.g. 60"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Min Total Inflow (36 months) (RM)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="bankStatementConfig.BANK_STATEMENT_MIN_TOTAL_INFLOW"
                      placeholder="e.g. 2000"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- Max Monthly Affordable Section -->
              <div class="mb-4">
                <h6 class="text-uppercase text-muted fw-semibold mb-1">
                  <i class="ri-calculator-line me-1"></i> Max Monthly Affordable Settings
                </h6>
                <p class="text-muted fs-12 mb-3">
                  Set percentage of gross salary/savings used to calculate customer device eligibility.
                </p>

                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Affordable Percentage (%)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model="maxMonthlyAffordableConfig.MAX_MONTHLY_AFFORDABLE_PERCENTAGE"
                      placeholder="e.g. 7"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <!-- Save Button -->
              <div class="text-end mt-4 pt-3 border-top">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="saveConfigurations"
                  :disabled="savingConfigs"
                >
                  <span v-if="savingConfigs">
                    <i class="ri-loader-4-line me-1"></i> Saving...
                  </span>
                  <span v-else>
                    <i class="ri-save-line me-1"></i> Save Configuration
                  </span>
                </button>
              </div>

            </div>

            <!-- Empty State: no program selected -->
            <div v-else class="text-center py-5 text-muted">
              <i class="ri-settings-4-line fs-40 d-block mb-2"></i>
              <p>Please select a program to view and edit its credit segmentation configuration.</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
